import { formatDonorApiError } from '../donorFormUtils';
import { sdk } from '../sdk';
import { getErrorMessage } from '../utils/errorMessage';

export interface GuestDonor {
  _id: string;
  guestId: string;
  name: string;
  fatherOrHusbandName: string;
  relation: 'Father' | 'Husband';
  phone: string;
  email?: string;
  officeId: string | {
    _id: string;
    name: string;
    officeNo: number;
  };
  cnic?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GuestDonorsResponse {
  success: boolean;
  data: GuestDonor[];
  message: string;
}

export interface GuestDonorResponse {
  success: boolean;
  data: GuestDonor;
  message: string;
}

export interface CreateGuestDonorData {
  name: string;
  fatherOrHusbandName: string;
  relation: 'Father' | 'Husband';
  phone: string;
  email?: string;
  address?: string;
  officeId: string;
}

export interface UpdateGuestDonorData extends Partial<CreateGuestDonorData> {
  cnic?: string;
}

export interface ConvertGuestDonorData {
  guestId: string;
  cnic: string;
  /** Optional fallback for legacy guest records missing office; normally omitted. */
  officeId?: string;
}

export class GuestDonorsAPI {
  // Get all guest donors
  static async getAllGuestDonors(params?: { page?: number; limit?: number; search?: string; officeId?: string }): Promise<{ data: GuestDonor[]; pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.officeId) queryParams.append('officeId', params.officeId);
      
      const url = `/api/v1/guest-donors${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await sdk.client.get(url);
      
      // If response is empty or undefined, return empty array
      if (!response) {
        return { data: [] };
      }
      
      // Check if the response has the expected structure
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch guest donors');
      }
      
      // Return data with pagination info if available
      return {
        data: response.data || [],
        pagination: response.pagination
      };
    } catch (error) {
      throw error;
    }
  }

  // Get guest donor by ID
  static async getGuestDonorById(id: string): Promise<GuestDonor> {
    try {
      const response = await sdk.client.get(`/api/v1/guest-donors/${id}`);
      const result: GuestDonorResponse = response.data;
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch guest donor');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Get guest donor by guestId (string ID like G001)
  static async getGuestDonorByGuestId(guestId: string): Promise<GuestDonor> {
    try {
      const response = await sdk.client.get(`/api/v1/guest-donors/search-by-guest-id/${guestId}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch guest donor');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 404) {
        throw new Error('Guest donor not found');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to fetch guest donor'));
      }
    }
  }

  // Create new guest donor
  static async createGuestDonor(data: CreateGuestDonorData): Promise<GuestDonor> {
    try {
      const response = await sdk.client.post('/api/v1/guest-donors', data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create guest donor');
      }
      
      return response.data;
    } catch (error: unknown) {
      throw new Error(formatDonorApiError(error, 'Failed to create guest donor. Please try again.'));
    }
  }

  // Update guest donor
  static async updateGuestDonor(id: string, data: UpdateGuestDonorData): Promise<GuestDonor> {
    try {
      const response = await sdk.client.put(`/api/v1/guest-donors/${id}`, data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update guest donor');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Invalid data provided. Please check your input and try again.');
      } else if (error?.response?.status === 404) {
        throw new Error('Guest donor not found.');
      } else if (error?.response?.status === 409) {
        throw new Error('A guest donor with this phone number already exists.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to update guest donor. Please try again.'));
      }
    }
  }

  // Delete guest donor
  static async deleteGuestDonor(id: string): Promise<void> {
    try {
      const response = await sdk.client.delete(`/api/v1/guest-donors/${id}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete guest donor');
      }
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Cannot delete guest donor. Guest donor may have associated donations. Please check and try again.');
      } else if (error?.response?.status === 404) {
        throw new Error('Guest donor not found.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to delete guest donor. Please try again.'));
      }
    }
  }

  // Convert guest donor to permanent donor
  static async convertGuestDonorToPermanent(data: ConvertGuestDonorData): Promise<any> {
    try {
      const response = await sdk.client.post('/api/v1/guest-donors/convert', data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to convert guest donor');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Invalid data provided. Please check your input and try again.');
      } else if (error?.response?.status === 404) {
        throw new Error('Guest donor not found.');
      } else if (error?.response?.status === 409) {
        throw new Error('A donor with this CNIC already exists.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to convert guest donor. Please try again.'));
      }
    }
  }

  // Search guest donors by various criteria
  static async searchGuestDonors(searchTerm: string, officeId?: string): Promise<GuestDonor[]> {
    try {
      const params = new URLSearchParams({ search: searchTerm });
      if (officeId) params.append('officeId', officeId);
      
      const response = await sdk.client.get(`/guest-donors/search?${params}`);
      return response.data.data || [];
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to search guest donors'));
    }
  }

  // Search guest donor by phone
  static async searchGuestDonorByPhone(phone: string): Promise<any> {
    try {
      const response = await sdk.client.get(`/api/v1/guest-donors/search-by-phone/${encodeURIComponent(phone)}`);
      
      // Return the response in the expected format with success and data properties
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          data: null
        };
      }
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Could not complete the search. Please try again.'));
    }
  }

  // Get guest donor statistics
  static async getGuestDonorStats(): Promise<any> {
    try {
      const response = await sdk.client.get('/api/v1/guest-donors/stats');
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch guest donor statistics');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(getErrorMessage(error, 'Failed to fetch guest donor statistics'));
      }
    }
  }
}
